import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { withRetry } from "@/utils/api-retry";
import { createErrorMessage } from "@/utils/create-error-message";

type Params = {
  symbol: string;
  resolution: string;
  fromTime: number;
  toTime: number;
  countBack?: number;
};

const tradingViewHistorySchema = z.object({
  data: z.object({
    c: z.array(z.number().nullable()).optional(),
    h: z.array(z.number().nullable()).optional(),
    l: z.array(z.number().nullable()).optional(),
    o: z.array(z.number().nullable()).optional(),
    s: z.union([z.literal("ok"), z.literal("no data"), z.literal("error")]),
    t: z.array(z.number().nullable()).optional(),
    v: z.array(z.number().nullable()).optional(),
  }),
  message: z.string(),
});

export type TradingViewHistory = z.infer<typeof tradingViewHistorySchema>;

export const fetchSymbolHistory = async ({
  symbol,
  resolution,
  fromTime,
  toTime,
  countBack,
}: Params): Promise<TradingViewHistory["data"]> => {
  const url = new URL(`${NIKE_URL}/tradingview-chart/history`);
  url.searchParams.set("symbol", symbol);
  url.searchParams.set("resolution", resolution);
  url.searchParams.set("fromTime", fromTime.toString());
  url.searchParams.set("toTime", toTime.toString());
  if (countBack) url.searchParams.set("countBack", countBack.toString());
  const res = await fetch(url.toString(), {
    method: "GET",
    cache: "no-cache",
  });
  if (!res.ok) {
    const errorMessage = await createErrorMessage(url.toString(), res);
    throw new Error(errorMessage);
  }
  return tradingViewHistorySchema.parse(await res.json()).data;
};

export const getSymbolHistory = (params: Params) =>
  withRetry(() => fetchSymbolHistory(params));
